import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const copyByLang = {
  es: {
    title: "Contacto | Puerto Rico Esports League",
    heroEyebrow: "Contacto",
    heroTitle: "Hablemos.",
    heroBody:
      "Si quieres hablar con PREL sobre auspicios, prensa, activaciones, contenido, colaboraciones o una oportunidad estratégica, este es el mejor lugar para empezar.",
    highlights: [
      {
        title: "Marcas y alianzas",
        body: "Conversaciones para marcas, integraciones, auspicios, activaciones y propuestas con potencial real dentro del ecosistema PREL.",
      },
      {
        title: "Prensa y acercamientos estratégicos",
        body: "Solicitudes de prensa, colaboraciones institucionales, alianzas estratégicas y conversaciones de crecimiento alrededor de la industria.",
      },
      {
        title: "Canalización clara",
        body: "Este espacio está pensado para llevar cada solicitud al área correcta sin perder contexto ni enfriar la conversación.",
      },
    ],
    formEyebrow: "Solicitudes comerciales y estratégicas",
    formTitle: "Cuéntanos qué necesitas",
    formBody: "Completa el formulario y el equipo de PREL canalizará tu solicitud al área correcta.",
    reasons: [
      "Auspicio / alianza de marca",
      "Prensa / medios",
      "Activación en evento",
      "Alianza estratégica",
      "Contenido / producción",
      "Consulta general",
    ],
    placeholders: {
      name: "Nombre",
      email: "Email",
      phone: "Teléfono",
      company: "Empresa / Organización",
      message: "Cuéntanos sobre tu oportunidad, meta o solicitud",
    },
    sending: "Enviando...",
    submit: "Enviar solicitud",
    success: "Gracias. Tu solicitud fue enviada correctamente.",
    error: "No se pudo enviar en este momento. Escríbenos directamente a info@puertoricoesportleague.com.",
  },
  en: {
    title: "Contact | Puerto Rico Esports League",
    heroEyebrow: "Contact",
    heroTitle: "Let's talk.",
    heroBody:
      "If you want to talk with PREL about sponsorships, press, activations, content, collaborations, or a strategic opportunity, this is the best place to start.",
    highlights: [
      {
        title: "Brands and partnerships",
        body: "Conversations for brands, integrations, sponsorships, activations, and proposals with real upside inside the PREL ecosystem.",
      },
      {
        title: "Press and strategic outreach",
        body: "Press requests, institutional collaborations, strategic partnerships, and growth conversations around the industry.",
      },
      {
        title: "Clear routing",
        body: "This space is designed to send each request to the right area without losing context or slowing down the conversation.",
      },
    ],
    formEyebrow: "Commercial and strategic inquiries",
    formTitle: "Tell us what you need",
    formBody: "Complete the form and the PREL team will route your request to the right area.",
    reasons: [
      "Sponsorship / brand partnership",
      "Press / media",
      "Event activation",
      "Strategic partnership",
      "Content / production",
      "General inquiry",
    ],
    placeholders: {
      name: "Name",
      email: "Email",
      phone: "Phone",
      company: "Company / Organization",
      message: "Tell us about your opportunity, goal, or request",
    },
    sending: "Sending...",
    submit: "Send inquiry",
    success: "Thanks. Your inquiry was sent successfully.",
    error: "We couldn't send it right now. Please email us directly at info@puertoricoesportleague.com.",
  },
};

function createContactForm(lang) {
  const copy = copyByLang[lang];
  return {
    name: "",
    email: "",
    phone: "",
    company: "",
    reason: copy.reasons[0],
    message: "",
  };
}

export default function ContactPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "es";
  const copy = copyByLang[lang];
  const [contactForm, setContactForm] = useState(() => createContactForm(lang));
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactMessage, setContactMessage] = useState("");

  useEffect(() => {
    document.title = copy.title;
  }, [copy.title]);

  useEffect(() => {
    setContactForm((prev) => {
      const prevLang = lang === "en" ? "es" : "en";
      const prevReasons = copyByLang[prevLang].reasons;
      const nextReasons = copy.reasons;
      const nextReason = nextReasons.includes(prev.reason)
        ? prev.reason
        : prevReasons.includes(prev.reason)
          ? nextReasons[prevReasons.indexOf(prev.reason)] || nextReasons[0]
          : nextReasons[0];
      return { ...prev, reason: nextReason };
    });
  }, [copy.reasons, lang]);

  const updateContactField = (field, value) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    setContactMessage("");

    try {
      const response = await fetch("/api/contact-commercial", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contactForm),
      });

      if (!response.ok) throw new Error("Request failed");

      setContactMessage(copy.success);
      setContactForm(createContactForm(lang));
    } catch (error) {
      setContactMessage(copy.error);
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#06070A] text-white">
      <SiteHeader current="/contacto" />

      <main>
        <section className="max-w-[1400px] mx-auto px-6 xl:px-8 pt-12 pb-14">
          <p className="text-xs tracking-[0.3em] uppercase text-[#CE1126]">{copy.heroEyebrow}</p>
          <h1 className="mt-4 text-[clamp(2.6rem,8vw,6rem)] font-black leading-[0.9] uppercase">
            {copy.heroTitle}
          </h1>
          <p className="mt-6 max-w-4xl text-slate-300 text-lg leading-relaxed">
            {copy.heroBody}
          </p>
        </section>

        <section className="max-w-[1400px] mx-auto px-6 xl:px-8 pb-10 grid lg:grid-cols-[1fr_1.1fr] gap-6 items-start">
          <div className="grid gap-4">
            {copy.highlights.map((item) => (
              <article key={item.title} className="border border-white/10 bg-[#111722] p-6 md:p-7">
                <h2 className="text-xl md:text-2xl font-bold">{item.title}</h2>
                <p className="mt-3 text-slate-300 leading-relaxed">{item.body}</p>
              </article>
            ))}
          </div>

          <section className="border border-white/10 bg-[#111722] p-7 md:p-8">
            <p className="text-xs uppercase tracking-[0.3em] text-cyan-300 mb-2">{copy.formEyebrow}</p>
            <h3 className="text-2xl font-black uppercase">{copy.formTitle}</h3>
            <p className="text-slate-300 mt-2">{copy.formBody}</p>

            <form onSubmit={submitContact} className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required value={contactForm.name} onChange={(e) => updateContactField("name", e.target.value)} placeholder={copy.placeholders.name} className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2" />
              <input required type="email" value={contactForm.email} onChange={(e) => updateContactField("email", e.target.value)} placeholder={copy.placeholders.email} className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2" />
              <input value={contactForm.phone} onChange={(e) => updateContactField("phone", e.target.value)} placeholder={copy.placeholders.phone} className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2" />
              <input value={contactForm.company} onChange={(e) => updateContactField("company", e.target.value)} placeholder={copy.placeholders.company} className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2" />
              <select required value={contactForm.reason} onChange={(e) => updateContactField("reason", e.target.value)} className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2 md:col-span-2">
                {copy.reasons.map((reason) => <option key={reason}>{reason}</option>)}
              </select>
              <textarea required value={contactForm.message} onChange={(e) => updateContactField("message", e.target.value)} placeholder={copy.placeholders.message} rows={5} className="md:col-span-2 w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2" />
              <button type="submit" disabled={contactSubmitting} className="md:col-span-2 rounded-lg bg-cyan-400 text-black font-semibold px-3 py-3 disabled:opacity-60">
                {contactSubmitting ? copy.sending : copy.submit}
              </button>
            </form>

            {contactMessage ? <p className="text-sm text-slate-300 mt-3">{contactMessage}</p> : null}
          </section>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
